import React, { useRef, useState } from 'react'
import { IconButton, InputAdornment, TextField, Tooltip } from '@material-ui/core'
import ClearIcon from '@material-ui/icons/Clear'
import MicIcon from '@material-ui/icons/Mic'
import MicOffIcon from '@material-ui/icons/MicOff'
import SearchIcon from '@material-ui/icons/Search'

/**
 * فیلد جست‌وجو با «میکروفن» (ورود صوتی) و «X» (پاک‌کردن).
 *
 * • ورود صوتی با Web Speech API (کروم/اج) و زبان fa-IR — اگر مرورگر پشتیبانی
 *   نکند، دکمهٔ میکروفن نمایش داده نمی‌شود (بدون خطا).
 * • «X» فقط وقتی متن وجود دارد نشان داده می‌شود.
 * • همهٔ props معمولِ TextField پاس می‌شوند (value/onChange/className/style/...).
 *
 * نکته: برای سازگاری، هنگام تشخیص گفتار رویدادی شبیه `onChange` معمول
 * (`{ target: { value } }`) صدا زده می‌شود تا همهٔ مصرف‌کننده‌ها بدون تغییر کار کنند.
 */
const SearchField = ({
  value,
  onChange,
  placeholder = 'جست‌وجو...',
  lang = 'fa-IR',
  showSearchIcon = true,
  ...rest
}) => {
  const [listening, setListening] = useState(false)
  const recRef = useRef(null)

  const SpeechRec = typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null
  const supported = !!SpeechRec

  const emit = (text) => {
    try {
      onChange({ target: { value: text } })
    } catch (e) {
      /* ignore */
    }
  }

  const startVoice = () => {
    if (!supported) return
    try {
      const rec = new SpeechRec()
      rec.lang = lang
      rec.interimResults = false
      rec.maxAlternatives = 1
      rec.continuous = false
      rec.onresult = (e) => {
        let text = ''
        try {
          text = e.results[0][0].transcript || ''
        } catch (err) {
          text = ''
        }
        if (text) emit(text.trim())
      }
      rec.onend = () => setListening(false)
      rec.onerror = () => setListening(false)
      recRef.current = rec
      setListening(true)
      rec.start()
    } catch (e) {
      setListening(false)
    }
  }

  const stopVoice = () => {
    try {
      if (recRef.current) recRef.current.stop()
    } catch (e) {
      /* ignore */
    }
    setListening(false)
  }

  return (
    <TextField
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      variant="outlined"
      size="small"
      InputProps={{
        startAdornment: showSearchIcon ? (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" style={{ color: '#90a4ae' }} />
          </InputAdornment>
        ) : undefined,
        endAdornment: (
          <InputAdornment position="end">
            {value !== '' && value !== null && value !== undefined && (
              <Tooltip title="پاک‌کردن">
                <IconButton size="small" onClick={() => emit('')} aria-label="پاک‌کردن">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {supported && (
              <Tooltip title={listening ? 'توقف ضبط' : 'جست‌وجوی صوتی'}>
                <IconButton
                  size="small"
                  onClick={listening ? stopVoice : startVoice}
                  aria-label="جست‌وجوی صوتی"
                  style={{ color: listening ? '#c62828' : '#546e7a' }}
                >
                  {listening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
          </InputAdornment>
        ),
      }}
      {...rest}
    />
  )
}

export default SearchField
